import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronLeft, Search, Flag, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { useBackTarget } from '../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";
const BRAND = 'var(--accent-purple)';

const ISSUES = [
  { id: 'inappropriate', label: 'Inappropriate content' },
  { id: 'misleading', label: 'Misleading or fake info' },
  { id: 'unsafe', label: 'Unsafe behaviour or harassment' },
  { id: 'service', label: 'Refused or poor service' },
  { id: 'fraud', label: 'Fraud / overcharging' },
  { id: 'other', label: 'Something else' },
];

export default function SupportReportVenuePage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const token = useMemo(() => localStorage.getItem('token'), []);

  const [venues, setVenues] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [issue, setIssue] = useState(ISSUES[0].id);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const loadVenues = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/venues`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setVenues(Array.isArray(res.data) ? res.data : (res.data?.venues || []));
    } catch (_) {
      // Non-blocking — user can still report without a venue list.
    }
  }, [token]);

  useEffect(() => { loadVenues(); }, [loadVenues]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return venues.slice(0, 8);
    return venues
      .filter((v) => (v.name || v.restaurant_name || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, venues]);

  const handleSubmit = async () => {
    if (!selectedVenue) {
      toast.error('Please select a venue');
      return;
    }
    if (details.trim().length < 10) {
      toast.error('Please describe the issue (at least 10 characters)');
      return;
    }
    setSubmitting(true);
    try {
      const issueLabel = ISSUES.find((i) => i.id === issue)?.label || issue;
      const venueName = selectedVenue.name || selectedVenue.restaurant_name || '(unknown)';
      const message =
        `Venue: ${venueName} (id: ${selectedVenue.id})\n` +
        `Issue: ${issueLabel}\n\n` +
        `Details:\n${details.trim()}`;
      await axios.post(
        `${API}/support/requests`,
        { reason: 'Report a venue', message },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSubmitted(true);
      toast.success('Report received — our team is reviewing it');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="app-shell"
        data-testid="support-report-venue-success"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--app-bg)',
          padding: 24,
        }}
      >
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.35 }}
          style={{ textAlign: 'center', maxWidth: 320 }}
        >
          <CheckCircle2 size={62} color={BRAND} strokeWidth={1.7} />
          <h1
            style={{
              fontFamily: FONT,
              fontSize: 24,
              color: 'var(--text)',
              margin: '14px 0 6px',
            }}
          >
            Report received
          </h1>
          <p
            style={{
              fontFamily: SUB_FONT,
              color: 'var(--text-2)',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            Our trust &amp; safety team will review your report confidentially. We&apos;ll follow up via Support if we need more info.
          </p>
          <button
            data-testid="support-report-venue-done-button"
            onClick={() => navigate('/support')}
            style={{
              marginTop: 22,
              padding: '12px 22px',
              border: 'none',
              borderRadius: 999,
              background: 'var(--accent-purple)',
              color: '#FFFFFF',
              fontFamily: FONT,
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 12px 24px rgba(196, 18, 48, 0.16)',
            }}
          >
            Back to Support
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="app-shell"
      data-testid="support-report-venue-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        backgroundImage:
          'radial-gradient(ellipse 600px 360px at 50% -140px, rgba(196, 18, 48, 0.10) 0%, transparent 60%)',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 390,
          display: 'flex',
          justifyContent: 'space-between',
          padding: '14px 0 0',
        }}
      >
        <button
          onClick={goBack}
          data-testid="support-report-venue-back-button"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            borderRadius: 999,
            cursor: 'pointer',
            padding: 6,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: 'var(--premium-shadow-nested)',
          }}
        >
          <ChevronLeft size={22} color="var(--text)" strokeWidth={2} />
        </button>
        <NotificationBellButton
          buttonTestId="support-report-venue-bell-button"
          popupTestId="support-report-venue-notification-popup"
        />
      </div>

      <RouteTransitionLogo width={80} height={72} style={{ marginTop: 18 }} />
      <h1
        style={{
          fontFamily: FONT,
          fontSize: 24,
          fontWeight: 400,
          color: 'var(--text)',
          margin: '8px 0 2px',
        }}
      >
        Report a Venue
      </h1>
      <p
        style={{
          margin: 0,
          fontFamily: SUB_FONT,
          fontSize: 13,
          color: 'var(--text-2)',
          textAlign: 'center',
          maxWidth: 290,
          lineHeight: 1.45,
        }}
      >
        Reports are handled confidentially by our Trust &amp; Safety team.
      </p>

      <div
        style={{
          width: '100%',
          maxWidth: 360,
          marginTop: 18,
          background: 'var(--surface)',
          borderRadius: 22,
          border: '1px solid rgba(196, 18, 48, 0.06)',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)',
          padding: '16px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {/* Venue picker */}
        <div>
          <label
            style={{
              display: 'block',
              fontFamily: SUB_FONT,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-2)',
              marginBottom: 8,
            }}
          >
            Which venue?
          </label>
          <div style={{ position: 'relative' }}>
            <Search
              size={15}
              color="#7A6E96"
              style={{ position: 'absolute', left: 12, top: 12 }}
            />
            <input
              data-testid="support-report-venue-search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (selectedVenue) setSelectedVenue(null);
              }}
              placeholder="Search venues by name…"
              style={{
                width: '100%',
                background: 'var(--surface-2, var(--surface))',
                border: '1px solid var(--card-border)',
                borderRadius: 14,
                padding: '11px 14px 11px 36px',
                fontFamily: SUB_FONT,
                fontSize: 13.5,
                color: 'var(--text)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {!selectedVenue && filtered.length > 0 && (
            <div
              style={{
                marginTop: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                maxHeight: 180,
                overflowY: 'auto',
              }}
            >
              {filtered.map((v) => (
                <button
                  key={v.id}
                  data-testid={`support-report-venue-option-${v.id}`}
                  onClick={() => {
                    setSelectedVenue(v);
                    setSearch(v.name || v.restaurant_name || '');
                  }}
                  style={{
                    textAlign: 'left',
                    padding: '8px 12px',
                    background: 'var(--surface)',
                    border: '1px solid rgba(196, 18, 48, 0.06)',
                    borderRadius: 10,
                    fontFamily: SUB_FONT,
                    fontSize: 13,
                    color: 'var(--text)',
                    cursor: 'pointer',
                  }}
                >
                  {v.name || v.restaurant_name || '(unnamed)'}
                </button>
              ))}
            </div>
          )}
          {selectedVenue && (
            <div
              data-testid="support-report-venue-selected"
              style={{
                marginTop: 8,
                background: 'rgba(196, 18, 48, 0.06)',
                border: `1px solid ${BRAND}`,
                borderRadius: 12,
                padding: '8px 12px',
                fontFamily: SUB_FONT,
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Flag size={13} color={BRAND} strokeWidth={2.2} />
              {selectedVenue.name || selectedVenue.restaurant_name}
            </div>
          )}
        </div>

        {/* Issue category */}
        <div>
          <label
            style={{
              display: 'block',
              fontFamily: SUB_FONT,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-2)',
              marginBottom: 8,
            }}
          >
            What&apos;s the issue?
          </label>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {ISSUES.map((it) => {
              const active = issue === it.id;
              return (
                <button
                  key={it.id}
                  data-testid={`support-report-venue-issue-${it.id}`}
                  onClick={() => setIssue(it.id)}
                  style={{
                    padding: '10px 12px',
                    background: active ? BRAND : 'var(--surface-2)',
                    color: active ? '#FFFFFF' : 'var(--text)',
                    border: active ? `1px solid ${BRAND}` : '1px solid var(--card-border)',
                    borderRadius: 12,
                    fontFamily: SUB_FONT,
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 180ms ease',
                    boxShadow: active ? 'var(--premium-shadow-main)' : 'none',
                  }}
                >
                  {it.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Details */}
        <div>
          <label
            style={{
              display: 'block',
              fontFamily: SUB_FONT,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-2)',
              marginBottom: 8,
            }}
          >
            Tell us what happened
          </label>
          <textarea
            data-testid="support-report-venue-details"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            rows={5}
            placeholder="Describe the situation. Include dates, times and anyone involved if you can."
            style={{
              width: '100%',
              border: '1px solid var(--card-border)',
              borderRadius: 14,
              padding: '12px 14px',
              fontFamily: SUB_FONT,
              fontSize: 14,
              resize: 'vertical',
              minHeight: 110,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <button
          data-testid="support-report-venue-submit"
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%',
            padding: '12px 14px',
            border: 'none',
            borderRadius: 14,
            background: submitting
              ? 'var(--accent-surface)'
              : 'var(--accent-purple)',
            color: '#FFFFFF',
            fontFamily: FONT,
            fontSize: 16,
            fontWeight: 700,
            cursor: submitting ? 'wait' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            boxShadow: 'var(--premium-shadow-main)',
          }}
        >
          <Send size={15} strokeWidth={2.2} />
          {submitting ? 'Sending…' : 'Submit Report'}
        </button>
      </div>
    </div>
  );
}
